import { ADMIN_STREAMS, type AdminStream } from '@molt/shared';
import { useRef, type KeyboardEvent, type ReactNode } from 'react';

import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Notice } from '@/components/Notice';
import { Skeleton } from '@/components/Skeleton';
import { TextField } from '@/components/TextField';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * SA-9 — the platform admin console's frame: title, the four tabs, the active
 * tab's filters and its panel.
 *
 * Presentational, like `PowerUpDirectory`: `routes/admin.tsx` owns the query
 * string, the session gate and every fetch, and hands this component values and
 * callbacks. Built from the shipped primitives only; the tab strip is the
 * `SearchResultsPage` adjacent-products strip's markup, not a new control.
 *
 * ⚠ The tabs are real `role="tab"` buttons over ONE panel, because the address
 * is the state: switching a tab replaces the query string, and the route renders
 * the newly selected tab's table into the same `tabpanel`. Arrow keys move
 * between tabs (the WAI-ARIA tabs pattern, activation on focus) so a keyboard
 * user does not tab through four stops to reach the search field.
 */

export const ADMIN_TAB_LABEL_KEYS: Readonly<Record<AdminStream, string>> = {
  users: 'admin.tabs.users',
  workspaces: 'admin.tabs.workspaces',
  boards: 'admin.tabs.boards',
  audit: 'admin.tabs.audit',
};

const TAB_CLASS =
  'rounded-card border px-3 py-1.5 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';
const TAB_SELECTED = 'border-accent bg-accent text-accent-ink';
const TAB_IDLE = 'border-border-strong bg-surface-raised text-ink hover:bg-surface';

/** The shipped native `<select>` look (`InboxFileDialog.tsx`), shared by every console filter. */
export const ADMIN_SELECT_CLASS =
  'h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent';

export const ADMIN_PANEL_ID = 'admin-console-panel';

export interface AdminConsoleProps {
  readonly tab: AdminStream;
  readonly onTabChange: (tab: AdminStream) => void;
  /** The active tab's search field and filters. */
  readonly filters: ReactNode;
  /** The active tab's list, usually an `AdminPagedPanel`. */
  readonly children: ReactNode;
}

export function AdminConsole({ tab, onTabChange, filters, children }: AdminConsoleProps) {
  const { t } = useInterfaceLanguage();
  const tabRefs = useRef<Partial<Record<AdminStream, HTMLButtonElement | null>>>({});

  function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, current: AdminStream) {
    const index = ADMIN_STREAMS.indexOf(current);
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : null;
    const edge =
      event.key === 'Home' ? 0 : event.key === 'End' ? ADMIN_STREAMS.length - 1 : null;
    if (step === null && edge === null) return;
    event.preventDefault();
    const nextIndex =
      edge ?? (index + (step ?? 0) + ADMIN_STREAMS.length) % ADMIN_STREAMS.length;
    const next = ADMIN_STREAMS[nextIndex];
    if (next === undefined) return;
    tabRefs.current[next]?.focus();
    onTabChange(next);
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 py-2">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-3xl font-semibold text-ink">
          {t('admin.route.title')}
        </h1>
        <p className="max-w-3xl text-sm text-ink-muted">{t('admin.route.description')}</p>
      </header>

      <div
        role="tablist"
        aria-label={t('admin.tabs.label')}
        className="flex flex-wrap gap-2"
      >
        {ADMIN_STREAMS.map((stream) => (
          <button
            key={stream}
            ref={(node) => {
              tabRefs.current[stream] = node;
            }}
            id={`admin-tab-${stream}`}
            type="button"
            role="tab"
            aria-selected={stream === tab}
            aria-controls={ADMIN_PANEL_ID}
            tabIndex={stream === tab ? 0 : -1}
            className={cn(TAB_CLASS, stream === tab ? TAB_SELECTED : TAB_IDLE)}
            onClick={() => onTabChange(stream)}
            onKeyDown={(event) => onTabKeyDown(event, stream)}
          >
            {t(ADMIN_TAB_LABEL_KEYS[stream])}
          </button>
        ))}
      </div>

      <div
        id={ADMIN_PANEL_ID}
        role="tabpanel"
        aria-labelledby={`admin-tab-${tab}`}
        className="flex min-w-0 flex-col gap-4"
      >
        <div className="flex flex-wrap items-start gap-3">{filters}</div>
        {children}
      </div>
    </div>
  );
}

export interface AdminSearchFieldProps {
  readonly label: string;
  readonly hint: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** The trimmed term, or `''` to clear the search. */
  readonly onSubmit: (value: string) => void;
}

/** One search box per tab, submitted explicitly so typing does not refetch per keystroke. */
export function AdminSearchField({
  label,
  hint,
  value,
  onChange,
  onSubmit,
}: AdminSearchFieldProps) {
  const { t } = useInterfaceLanguage();
  return (
    <form
      role="search"
      className="flex w-full min-w-0 flex-wrap items-start gap-2 sm:w-auto sm:flex-1"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(value.trim());
      }}
    >
      <TextField
        type="search"
        label={label}
        hint={hint}
        value={value}
        wrapperClassName="min-w-0 flex-1 basis-56"
        onChange={(event) => onChange(event.currentTarget.value)}
        clearable
        onClear={() => {
          onChange('');
          onSubmit('');
        }}
      />
      <Button type="submit" variant="primary" className="mt-6">
        {t('admin.search.submit')}
      </Button>
    </form>
  );
}

export interface AdminPagedPanelProps {
  /** The first page has not arrived. */
  readonly loading: boolean;
  readonly error: string | undefined;
  readonly onRetry: () => void;
  readonly empty: boolean;
  readonly emptyTitle: string;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly loadMoreError: string | undefined;
  readonly onLoadMore: () => void;
  /** The table, rendered once there are rows. */
  readonly children: ReactNode;
}

/** Loading, failure, empty and "Load more" around one console table. */
export function AdminPagedPanel({
  loading,
  error,
  onRetry,
  empty,
  emptyTitle,
  hasMore,
  loadingMore,
  loadMoreError,
  onLoadMore,
  children,
}: AdminPagedPanelProps) {
  const { t } = useInterfaceLanguage();

  if (error !== undefined) {
    return (
      <EmptyState
        tone="error"
        size="panel"
        title={t('admin.error.title')}
        body={error}
        action={
          <Button type="button" onClick={onRetry}>
            {t('admin.error.retry')}
          </Button>
        }
      />
    );
  }

  if (loading) {
    return (
      <div aria-busy="true" className="flex flex-col gap-2">
        <span className="sr-only">{t('admin.loading')}</span>
        <Skeleton shape="row" count={6} />
      </div>
    );
  }

  if (empty) {
    return (
      <EmptyState
        tone="neutral"
        size="panel"
        title={emptyTitle}
        body={t('admin.empty.body')}
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {children}
      {loadMoreError === undefined ? null : (
        <Notice tone="bad" title={t('admin.loadMoreFailed')} body={loadMoreError} />
      )}
      {hasMore ? (
        <div className="flex justify-center">
          <Button
            type="button"
            busy={loadingMore}
            busyLabel={t('admin.loadingMore')}
            onClick={onLoadMore}
          >
            {t('admin.loadMore')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** The scroller every console table sits in: the page never scrolls sideways, the table may. */
export function AdminTableFrame({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <section
      aria-label={label}
      className="overflow-x-auto rounded-card border border-border-strong bg-surface"
    >
      <table className="w-full min-w-[48rem] border-collapse text-left text-card">
        {children}
      </table>
    </section>
  );
}

export const ADMIN_TH_CLASS = 'px-3 py-2 font-medium';
export const ADMIN_TD_CLASS = 'px-3 py-2 align-top';
export const ADMIN_THEAD_CLASS = 'bg-surface-raised text-xs uppercase text-ink-muted';
export const ADMIN_TR_CLASS = 'border-t border-border';

/** A small pill, the plan's badge markup — not `Badge`, which is count-only. */
export function AdminPill({
  children,
  tone = 'muted',
}: {
  readonly children: ReactNode;
  readonly tone?: 'muted' | 'warn' | 'bad' | 'accent';
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border bg-surface px-2 py-0.5 text-xs font-medium',
        tone === 'muted' && 'border-border text-ink-muted',
        tone === 'warn' && 'border-warn text-warn',
        tone === 'bad' && 'border-bad text-bad',
        tone === 'accent' && 'border-accent text-accent',
      )}
    >
      {children}
    </span>
  );
}
